import React, { useState, useEffect } from 'react';
import { db, auth } from '../../firebase';
import { ref, onValue, update } from 'firebase/database';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { FaArrowLeft } from 'react-icons/fa';

const ZpHeadEngineerDetails = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const [loading, setLoading] = useState(true);
    const [updating, setUpdating] = useState(false);
    const [formData, setFormData] = useState({
        surname: '',
        firstName: '',
        middleName: '',
        whatsappNo: '',
        email: '',
        dob: '',
        birthPlace: '',
        taluka: '',
        technicalQualification: '',
        trade: '',
        aadharNo: '',
        panNo: '',
        gstNo: '',
        societyName: '',
        universityName: '',
        passingYear: '',
        businessLocation: '',
        bankName: '',
        address: '',
        bankAddress: ''
    });

    useEffect(() => {
        const unsubscribe = auth.onAuthStateChanged((user) => {
            if (!user) navigate('/login');
        });

        if (id) {
            const engineerRef = ref(db, `ekamwatapzp/EngineerRegistrations/${id}`);
            const unsubData = onValue(engineerRef, (snapshot) => {
                if (snapshot.exists()) {
                    setFormData(prev => ({ ...prev, ...snapshot.val() }));
                } else {
                    toast.error("Engineer data not found");
                    navigate('/zp-head/engineers/new');
                }
                setLoading(false);
            });

            return () => {
                unsubscribe();
                unsubData();
            };
        }
    }, [id, navigate]);

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const handleUpdate = async (e) => {
        e.preventDefault();
        setUpdating(true);
        try {
            const engineerRef = ref(db, `ekamwatapzp/EngineerRegistrations/${id}`);
            await update(engineerRef, formData);
            toast.success("Profile Updated Successfully!");
        } catch (error) {
            toast.error("Error updating profile: " + error.message);
        } finally {
            setUpdating(false);
        }
    };

    if (loading) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-gray-50">
                <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-[#1a1a4d]"></div>
            </div>
        );
    }

    return (
        <div className="p-4 md:p-8 bg-[#f4f7fe] min-h-screen font-sans">
            {/* Header Area */}
            <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-8 gap-4">
                <div className="flex items-center gap-4">
                    <button 
                        onClick={() => navigate(-1)}
                        className="p-2.5 bg-white rounded-lg shadow-sm border border-gray-100 text-[#1a1a4d] hover:bg-gray-50 transition active:scale-95"
                        title="Back"
                    >
                        <FaArrowLeft />
                    </button>
                    <h1 className="text-xl font-bold text-gray-800">अर्ज फॉर्म पहा</h1>
                </div>
                <div className="text-sm font-medium text-gray-500">
                    सांगली जिल्हा परिषद  /  <span className="text-gray-400">अर्ज फॉर्म पहा</span>
                </div>
            </div>

            {/* Form Card */}
            <div className="bg-white rounded-xl shadow-lg border border-gray-100 p-8">
                <form onSubmit={handleUpdate} className="grid grid-cols-1 md:grid-cols-3 gap-6">
                    
                    {/* Surname */}
                    <div className="space-y-2">
                        <label className="text-[13px] font-bold text-gray-700 flex items-center gap-1">
                            अर्जदार आडनाव <span className="text-red-500">*</span>
                        </label>
                        <input 
                            type="text" name="surname" value={formData.surname} onChange={handleInputChange}
                            className="w-full border border-gray-200 rounded-lg px-4 py-2.5 text-sm focus:border-blue-500 outline-none transition"
                        />
                    </div>

                    {/* First Name */}
                    <div className="space-y-2">
                        <label className="text-[13px] font-bold text-gray-700 flex items-center gap-1">
                            पहिले नाव <span className="text-red-500">*</span>
                        </label>
                        <input 
                            type="text" name="firstName" value={formData.firstName} onChange={handleInputChange}
                            className="w-full border border-gray-200 rounded-lg px-4 py-2.5 text-sm focus:border-blue-500 outline-none transition"
                        />
                    </div>

                    {/* Middle Name */}
                    <div className="space-y-2">
                        <label className="text-[13px] font-bold text-gray-700 flex items-center gap-1">
                            वडीलांचे नांव <span className="text-red-500">*</span>
                        </label>
                        <input 
                            type="text" name="middleName" value={formData.middleName} onChange={handleInputChange}
                            className="w-full border border-gray-200 rounded-lg px-4 py-2.5 text-sm focus:border-blue-500 outline-none transition"
                        />
                    </div>

                    {/* WhatsApp */}
                    <div className="space-y-2">
                        <label className="text-[13px] font-bold text-gray-700 flex items-center gap-1">
                            व्हॉट्सॲप नंबर <span className="text-red-500">*</span>
                        </label>
                        <input 
                            type="text" name="whatsappNo" value={formData.whatsappNo} onChange={handleInputChange}
                            className="w-full border border-gray-200 rounded-lg px-4 py-2.5 text-sm focus:border-blue-500 outline-none transition"
                        />
                    </div>

                    {/* Email */}
                    <div className="space-y-2">
                        <label className="text-[13px] font-bold text-gray-700 flex items-center gap-1">
                            ई - मेल आयडी <span className="text-red-500">*</span>
                        </label>
                        <input 
                            type="email" name="email" value={formData.email} onChange={handleInputChange}
                            className="w-full border border-gray-200 rounded-lg px-4 py-2.5 text-sm focus:border-blue-500 outline-none transition"
                        />
                    </div>

                    {/* DOB */}
                    <div className="space-y-2">
                        <label className="text-[13px] font-bold text-gray-700 flex items-center gap-1">
                            जन्मतारीख
                        </label>
                        <input 
                            type="date" name="dob" value={formData.dob} onChange={handleInputChange}
                            className="w-full border border-gray-200 rounded-lg px-4 py-2.5 text-sm focus:border-blue-500 outline-none transition"
                        />
                    </div>

                    {/* Birth Place */}
                    <div className="space-y-2">
                        <label className="text-[13px] font-bold text-gray-700">जन्म स्थान</label>
                        <input 
                            type="text" name="birthPlace" value={formData.birthPlace} onChange={handleInputChange}
                            className="w-full border border-gray-200 rounded-lg px-4 py-2.5 text-sm focus:border-blue-500 outline-none transition"
                        />
                    </div>

                    {/* Taluka */}
                    <div className="space-y-2">
                        <label className="text-[13px] font-bold text-gray-700 flex items-center gap-1">
                            तालुका <span className="text-red-500">*</span>
                        </label>
                        <select 
                            name="taluka" value={formData.taluka} onChange={handleInputChange}
                            className="w-full border border-gray-200 rounded-lg px-4 py-2.5 text-sm focus:border-blue-500 outline-none transition appearance-none bg-no-repeat bg-[right_1rem_center]"
                            style={{ backgroundImage: 'url("data:image/svg+xml,%3Csvg xmlns=\'http://www.w3.org/2000/svg\' fill=\'none\' viewBox=\'0 0 24 24\' stroke=\'currentColor\'%3E%3Cpath stroke-linecap=\'round\' stroke-linejoin=\'round\' stroke-width=\'2\' d=\'M19 9l-7 7-7-7\'/%3E%3C/svg%3E")', backgroundSize: '1em' }}
                        >
                            <option value="">निवडा</option>
                            <option value="Miraj">मिरज</option>
                            <option value="Tasgaon">तासगाव</option>
                            <option value="Kavathe Mahankal">कवठे महांकाळ</option>
                            <option value="Walwa">वाळवा</option>
                            <option value="Shirala">शिराळा</option>
                            <option value="Khanapur">खानापूर</option>
                            <option value="Atpadi">आटपाडी</option>
                            <option value="Jat">जत</option>
                            <option value="Kadegaon">कडेगाव</option>
                            <option value="Palus">पलूस</option>
                        </select>
                    </div>

                    {/* Technical Qualification */}
                    <div className="space-y-2">
                        <label className="text-[13px] font-bold text-gray-700 flex items-center gap-1">
                            तांत्रिक पात्रता <span className="text-red-500">*</span>
                        </label>
                        <select 
                            name="technicalQualification" value={formData.technicalQualification} onChange={handleInputChange}
                            className="w-full border border-gray-200 rounded-lg px-4 py-2.5 text-sm focus:border-blue-500 outline-none transition appearance-none bg-no-repeat bg-[right_1rem_center]"
                            style={{ backgroundImage: 'url("data:image/svg+xml,%3Csvg xmlns=\'http://www.w3.org/2000/svg\' fill=\'none\' viewBox=\'0 0 24 24\' stroke=\'currentColor\'%3E%3Cpath stroke-linecap=\'round\' stroke-linejoin=\'round\' stroke-width=\'2\' d=\'M19 9l-7 7-7-7\'/%3E%3C/svg%3E")', backgroundSize: '1em' }}
                        >
                            <option value="">निवडा</option>
                            <option value="Degree">Degree</option>
                            <option value="Diploma">Diploma</option>
                        </select>
                    </div>

                    {/* Trade */}
                    <div className="space-y-2">
                        <label className="text-[13px] font-bold text-gray-700 flex items-center gap-1">
                            ट्रेड <span className="text-red-500">*</span>
                        </label>
                        <select 
                            name="trade" value={formData.trade} onChange={handleInputChange}
                            className="w-full border border-gray-200 rounded-lg px-4 py-2.5 text-sm focus:border-blue-500 outline-none transition appearance-none bg-no-repeat bg-[right_1rem_center]"
                            style={{ backgroundImage: 'url("data:image/svg+xml,%3Csvg xmlns=\'http://www.w3.org/2000/svg\' fill=\'none\' viewBox=\'0 0 24 24\' stroke=\'currentColor\'%3E%3Cpath stroke-linecap=\'round\' stroke-linejoin=\'round\' stroke-width=\'2\' d=\'M19 9l-7 7-7-7\'/%3E%3C/svg%3E")', backgroundSize: '1em' }}
                        >
                            <option value="">निवडा</option>
                            <option value="Civil">Civil</option>
                            <option value="Electrical">Electrical</option>
                        </select>
                    </div>

                    {/* Aadhar */}
                    <div className="space-y-2">
                        <label className="text-[13px] font-bold text-gray-700">आधार क्रमांक</label>
                        <input 
                            type="text" name="aadharNo" value={formData.aadharNo} onChange={handleInputChange}
                            className="w-full border border-gray-200 rounded-lg px-4 py-2.5 text-sm focus:border-blue-500 outline-none transition"
                        />
                    </div>

                    {/* PAN */}
                    <div className="space-y-2">
                        <label className="text-[13px] font-bold text-gray-700">पॅन क्रमांक</label>
                        <input 
                            type="text" name="panNo" value={formData.panNo} onChange={handleInputChange}
                            className="w-full border border-gray-200 rounded-lg px-4 py-2.5 text-sm focus:border-blue-500 outline-none transition"
                        />
                    </div>

                    {/* GST */}
                    <div className="space-y-2">
                        <label className="text-[13px] font-bold text-gray-700">जी.एस.टी क्रमांक</label>
                        <input 
                            type="text" name="gstNo" value={formData.gstNo} onChange={handleInputChange}
                            placeholder="जी.एस.टी क्रमांक प्रविष्ट करा"
                            className="w-full border border-gray-200 rounded-lg px-4 py-2.5 text-sm focus:border-blue-500 outline-none transition"
                        />
                    </div>

                    {/* Society Name */}
                    <div className="space-y-2">
                        <label className="text-[13px] font-bold text-gray-700">संस्थेचे नाव</label>
                        <input 
                            type="text" name="societyName" value={formData.societyName} onChange={handleInputChange}
                            placeholder="संस्थेचे नाव प्रविष्ट करा"
                            className="w-full border border-gray-200 rounded-lg px-4 py-2.5 text-sm focus:border-blue-500 outline-none transition"
                        />
                    </div>

                    {/* University Name */}
                    <div className="space-y-2">
                        <label className="text-[13px] font-bold text-gray-700">विद्यापीठाचे नाव</label>
                        <input 
                            type="text" name="universityName" value={formData.universityName} onChange={handleInputChange}
                            placeholder="विद्यापीठाचे नाव प्रविष्ट करा"
                            className="w-full border border-gray-200 rounded-lg px-4 py-2.5 text-sm focus:border-blue-500 outline-none transition"
                        />
                    </div>

                    {/* Passing Year */}
                    <div className="space-y-2">
                        <label className="text-[13px] font-bold text-gray-700">उत्तीर्ण वर्ष</label>
                        <input 
                            type="text" name="passingYear" value={formData.passingYear} onChange={handleInputChange}
                            className="w-full border border-gray-200 rounded-lg px-4 py-2.5 text-sm focus:border-blue-500 outline-none transition"
                        />
                    </div>

                    {/* Business Location */}
                    <div className="space-y-2">
                        <label className="text-[13px] font-bold text-gray-700 flex items-center gap-1">
                            व्यवसायाचे ठिकाण <span className="text-red-500">*</span>
                        </label>
                        <input 
                            type="text" name="businessLocation" value={formData.businessLocation} onChange={handleInputChange}
                            className="w-full border border-gray-200 rounded-lg px-4 py-2.5 text-sm focus:border-blue-500 outline-none transition"
                        />
                    </div>

                    {/* Bank Name */}
                    <div className="space-y-2">
                        <label className="text-[13px] font-bold text-gray-700 flex items-center gap-1">
                            बँकेचे नाव <span className="text-red-500">*</span>
                        </label>
                        <input 
                            type="text" name="bankName" value={formData.bankName} onChange={handleInputChange}
                            className="w-full border border-gray-200 rounded-lg px-4 py-2.5 text-sm focus:border-blue-500 outline-none transition"
                        />
                    </div>

                    {/* Address */}
                    <div className="md:col-span-3 space-y-2">
                        <label className="text-[13px] font-bold text-gray-700 flex items-center gap-1">
                            सध्याचा पत्ता <span className="text-red-500">*</span>
                        </label>
                        <textarea 
                            name="address" value={formData.address} onChange={handleInputChange}
                            rows="3"
                            className="w-full border border-gray-200 rounded-lg px-4 py-2.5 text-sm focus:border-blue-500 outline-none transition resize-none"
                        ></textarea>
                    </div>

                    {/* Bank Address */}
                    <div className="md:col-span-3 space-y-2">
                        <label className="text-[13px] font-bold text-gray-700">बँकेचा पत्ता</label>
                        <input 
                            type="text" name="bankAddress" value={formData.bankAddress} onChange={handleInputChange}
                            className="w-full border border-gray-200 rounded-lg px-4 py-2.5 text-sm focus:border-blue-500 outline-none transition"
                        />
                    </div>

                    {/* Action Buttons */}
                    <div className="md:col-span-3 flex items-center gap-3 pt-4">
                        <button 
                            type="submit"
                            disabled={updating}
                            className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-2.5 rounded-lg text-sm font-bold transition shadow-md active:scale-95 disabled:opacity-50"
                        >
                            {updating ? 'Updating...' : 'Update Profile'}
                        </button>
                        <button 
                            type="button"
                            onClick={() => navigate(-1)}
                            className="bg-emerald-500 hover:bg-emerald-600 text-white px-6 py-2.5 rounded-lg text-sm font-bold transition shadow-md active:scale-95"
                        >
                            Cancel
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default ZpHeadEngineerDetails;
